import React from 'react'
import { appConfirm } from '../../../../components/AppConfirmModal'

/**
 * AgentProvisioningTable
 * ----------------------
 * Displays all Bridge employees left-joined with agent mappings for the active App.
 *
 * Cleaned & Refactored UX:
 *  - Employee column: shows actual Department or Role (not generic "Agent").
 *  - App User ID column: shows email + multi-app badges directly below it.
 *  - Agent Mobile (PSTN) column: read-only phone number from Users table.
 *  - Status & SIP: clean provisioned status badge + SIP ID; SIP toggle removed.
 *  - Provision: opens ProvisionAgentModal to select target App and confirm.
 *  - Deprovision: opens appConfirm modal before removing agent mapping.
 */
export default function AgentProvisioningTable({
  app,
  apps = [],
  selectedAppId,
  onSelectApp,
  exophone,
  employees = [],
  employeesLoading = false,
  provisioningId,
  provisionMsg,
  setProvisionMsg,
  onRequestProvision,
  onDeprovisionAgent,
}) {
  const provisionedCount = employees.filter((e) => e.is_provisioned || e.is_mapped).length

  const handleDeprovision = async (emp) => {
    const empUserId = emp.id || emp.user_id
    const empName = emp.employee_name || emp.name || 'this employee'
    const appName = app?.app_name || 'this App'

    const confirmed = await appConfirm({
      title: 'Deprovision Agent',
      message: `Are you sure you want to deprovision "${empName}" from "${appName}"? They will no longer be able to make or receive VoIP calls under this App.`,
      confirmText: 'Deprovision',
      variant: 'danger',
    })

    if (confirmed) {
      onDeprovisionAgent(empUserId)
    }
  }

  return (
    <div className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/25 shadow-xs space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-outline-variant/15 pb-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-extrabold uppercase tracking-wider font-display text-on-surface">Agents Provisioning</h2>
            {apps.length > 1 && onSelectApp && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-on-surface-variant font-semibold">Target App:</span>
                <select
                  value={selectedAppId || ''}
                  onChange={(e) => onSelectApp(Number(e.target.value))}
                  className="px-2.5 py-1 rounded-xl border border-primary/40 bg-primary/5 text-primary text-xs font-bold focus:ring-2 focus:ring-primary focus:outline-none cursor-pointer"
                >
                  {apps.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.app_name || a.app_id}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <p className="text-xs text-on-surface-variant mt-0.5">
            Provision employees as SIP/WebRTC agents for <span className="font-bold text-primary">{app?.app_name || 'this App'}</span>. Shared ExoPhone: <span className="font-mono font-semibold">{exophone || '—'}</span>.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs font-semibold text-on-surface-variant shrink-0">
          <span>Provisioned here:</span>
          <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">
            {provisionedCount} / {employees.length}
          </span>
        </div>
      </div>

      {/* ── Status message ── */}
      {provisionMsg && (
        <div
          className={`p-3.5 rounded-xl border text-xs font-semibold flex items-center justify-between ${
            provisionMsg.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base">
              {provisionMsg.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span>{provisionMsg.text}</span>
          </div>
          <button
            type="button"
            onClick={() => setProvisionMsg(null)}
            className="text-on-surface-variant hover:text-on-surface ml-2"
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="overflow-x-auto rounded-2xl border border-outline-variant/25">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-surface-container-low/40 border-b border-outline-variant/25 text-[11px] font-extrabold uppercase tracking-wider text-black">
              <th className="py-3.5 px-4 text-black">Employee</th>
              <th className="py-3.5 px-4 text-black">App User ID (email)</th>
              <th className="py-3.5 px-4 text-black">Agent Mobile (PSTN)</th>
              <th className="py-3.5 px-4 text-black">Status &amp; SIP</th>
              <th className="py-3.5 px-4 text-right text-black">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/20 text-xs">
            {/* Loading state */}
            {employeesLoading && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-xs text-on-surface-variant">
                  <span className="material-symbols-outlined animate-spin text-2xl block mx-auto mb-2">progress_activity</span>
                  Loading employees…
                </td>
              </tr>
            )}

            {/* Empty state */}
            {!employeesLoading && employees.length === 0 && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-xs text-on-surface-variant">
                  No Bridge employees found.
                </td>
              </tr>
            )}

            {/* Employee rows */}
            {!employeesLoading &&
              employees.map((emp) => {
                const empUserId = emp.id || emp.user_id
                const isProvisioned = emp.is_provisioned || emp.is_mapped === true || emp.is_mapped === 1
                const displayName = emp.employee_name || emp.app_username || emp.name || 'Employee'
                const email = emp.email || emp.app_user_id || ''
                const phone = emp.phone_number || emp.phone || emp.agent_number || '—'
                const deptOrRole = emp.department || emp.role || 'Staff'
                const initials = displayName
                  .split(' ')
                  .filter(Boolean)
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)

                // Multi-app badges: all apps this employee is mapped under
                const provisionedApps = Array.isArray(emp.provisioned_apps) ? emp.provisioned_apps : []

                return (
                  <tr key={empUserId} className="hover:bg-surface-container-low/40 transition-colors align-top">
                    {/* Employee name + department/role */}
                    <td className="py-3.5 px-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                          {initials}
                        </div>
                        <div>
                          <h4 className="font-bold text-on-surface">{displayName}</h4>
                          <p className="text-[11px] text-on-surface-variant font-medium">
                            {deptOrRole}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* App user ID (email) + multi-app badges */}
                    <td className="py-3.5 px-4">
                      <span className="font-mono text-[11px] text-on-surface block">{email || '—'}</span>
                      {provisionedApps.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {provisionedApps.map((appName, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] font-semibold flex items-center gap-1"
                            >
                              <span className="material-symbols-outlined text-[11px]">dialpad</span>
                              {appName}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* Agent mobile number (PSTN) — read only from Users table */}
                    <td className="py-3.5 px-4">
                      <span className="font-mono text-on-surface text-xs font-semibold">
                        {phone}
                      </span>
                    </td>

                    {/* Status badge + SIP URI */}
                    <td className="py-3.5 px-4">
                      {isProvisioned ? (
                        <div className="space-y-1">
                          <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Provisioned
                          </span>

                          {(emp.device_contact_uri || emp.exotel_user_sid) && (
                            <p
                              className="text-[10px] font-mono text-primary truncate max-w-[180px]"
                              title={emp.device_contact_uri || emp.exotel_user_sid}
                            >
                              {emp.device_contact_uri || emp.exotel_user_sid}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200 text-[10px] font-bold uppercase tracking-wider">
                          Not provisioned
                        </span>
                      )}
                    </td>

                    {/* Action button */}
                    <td className="py-3.5 px-4 text-right">
                      {isProvisioned ? (
                        <button
                          type="button"
                          onClick={() => handleDeprovision(emp)}
                          className="px-3.5 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 text-xs font-semibold transition-colors"
                        >
                          Deprovision
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={provisioningId === empUserId}
                          onClick={() => onRequestProvision(emp)}
                          className="px-3.5 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-on-primary text-xs font-bold shadow-2xs transition-colors flex items-center gap-1.5 ml-auto disabled:opacity-60"
                        >
                          <span className="material-symbols-outlined text-sm">person_add</span>
                          <span>{provisioningId === empUserId ? 'Provisioning…' : 'Provision'}</span>
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
